import Vue from 'vue'
import { MutationTree } from 'vuex'
import * as types from './mutation-types'
import CartState from '../types/CartState'
import config from 'config'
import { timezoneTime } from '@vue-storefront/core/helpers'
import rootStore from '@vue-storefront/core/store'

const mutations: MutationTree<CartState> = {
  /**
   * Add product to cart
   * @param {Object} product data format for products is described in /doc/ElasticSearch data formats.md
   */
  [types.CART_ADD_ITEM] (state, { product }) {
    const record = state.cartItems.find(p => {
      let isSkuEqual = p.sku === product.sku
      let isOptionsEqual = true
      /** This huge if is here only to check if custom options are really added */
      if (
        'options' in p && p.options.length > 0
        && 'selected_options' in product && product.selected_options.length > 0
      ) {
        isOptionsEqual = JSON.stringify(p.options,['sku']) === JSON.stringify(product.selected_options,['sku'])
      }
      return (isSkuEqual && isOptionsEqual)
    })
    let newQty = null;
    if (product.stock.is_qty_decimal) {
      newQty = parseFloat(product.qty ? product.qty : 0.1);
      newQty = Math.round(newQty * 100) / 100;
    } else {
      newQty = parseFloat(product.qty ? product.qty : 1);
      newQty = Math.round(newQty);
    }
    if (!record) {
      let item = {
        ...product,
        qty: newQty
      }
      Vue.prototype.$bus.$emit('cart-before-add', { product: item })
      state.cartItems.push(item)
    } else {
      Vue.prototype.$bus.$emit('cart-before-update', { product: record })
      record.stock = product.stock;
      record.qty += newQty
      if (product.stock.is_qty_decimal) {
        record.qty = Math.round(record.qty * 100) / 100;
      } else {
        record.qty = Math.round(record.qty);
      }
    }
  },
  [types.CART_SET_ITEMS_HASH] (state, hash = null) {
    state.cartItemsHash = hash
  },
  [types.CART_SET_SYNC] (state) {
    state.cartServerLastSyncDate = timezoneTime().getTime()
  },
  [types.CART_SET_TOTALS_SYNC] (state) {
    state.cartServerLastTotalsSyncDate = timezoneTime().getTime()
  },
  [types.CART_DEL_ITEM] (state, { product, removeByParentSku = true }) {
    Vue.prototype.$bus.$emit('cart-before-delete', { items: state.cartItems })
    let count = 0
    state.groupedCart = state.groupedCart.filter(p => {
      let subtotal = 0
      p.items = p.items.filter(i => {
        let checkBySku = i.entity_id === product.entity_id && (i.item_hash === product.item_hash || removeByParentSku === false)
        let result = checkBySku
        if (!checkBySku) {
          count += i.qty
          subtotal += parseFloat((i.qty * i.price).toFixed(2))
        }
        return !result
      })
      p.subtotal = subtotal
      return p.items.length
    })
    // groupedCart is what drives the visible cart list, but cartItems (the raw list MiniAddToCart's
    // "already in cart" badge reads - see AddToCart.ts) was never pruned here, so a removed product
    // kept showing as still-in-cart anywhere its tile appears (same groupedCart/cartItems split
    // CART_LOAD_UPDATE had on the add side).
    state.cartItems = state.cartItems.filter(item => parseInt(item.entity_id) !== parseInt(product.entity_id))
    state.cartQuantity = count
    Vue.prototype.$bus.$emit('cart-after-delete', { items: state.cartItems })
  },
  [types.CART_DEL_NON_CONFIRMED_ITEM] (state, { product, removeByParentSku = true }) {
    Vue.prototype.$bus.$emit('cart-before-delete', { items: state.cartItems })
    state.cartItems = state.cartItems.filter(p => (p.sku !== product.sku && (p.parentSku !== product.sku || removeByParentSku === false)) || p.server_item_id/* it's confirmed if server_item_id is set */)
    Vue.prototype.$bus.$emit('cart-after-delete', { items: state.cartItems })
  },
  [types.CART_UPD_ITEM] (state, { product, qty }) {
    let count = 0
    let subtotal = 0
    let currentGroup = state.groupedCart.find(gr => gr.partner.id === product.partner_id)
    let record = currentGroup.items ? currentGroup.items.find(it => it.item_id === product.item_id) : { qty: 0, price: 0 }
    if (record && record.hasOwnProperty('item_id')) {
      Vue.prototype.$bus.$emit('cart-before-update', { product: record })
      record.qty = parseFloat(qty)
      if (product.stock.is_qty_decimal) {
        record.qty = Math.round(record.qty * 100) / 100;
      } else {
        record.qty = Math.round(record.qty);
      }
      currentGroup.items.forEach(it => {
        count += it.qty
        subtotal += parseFloat((it.qty * it.price).toFixed(2))
      })
      state.cartQuantity = count;
      currentGroup.subtotal = subtotal;
      if (currentGroup.delivery != null && currentGroup.partner.shipping.delivery_price_coverage.length) {
        let tiers = currentGroup.partner.shipping.delivery_price_coverage.sort((a, b) => b.from_cart_amount - a.from_cart_amount);
        let tier = tiers.find(t => t.from_cart_amount <= currentGroup.subtotal);
        if (tier) {
          let finalDelivery = currentGroup.delivery - tier.coverage_amount;
          currentGroup.final_delivery = finalDelivery < 0 ? 0 : finalDelivery;
        }
        if (!tier) {
          currentGroup.final_delivery = currentGroup.delivery
        }
      }
      Vue.prototype.$bus.$emit('cart-after-update', { product: record })
    }
  },
  [types.CART_UPD_ITEM_PROPS] (state, { product }) {
    let record = state.cartItems.find(p => (p.sku === product.sku || (p.server_item_id && p.server_item_id === product.server_item_id)))
    if (record) {
      Vue.prototype.$bus.$emit('cart-before-itemchanged', { item: record })
      record = Object.assign(record, product)
      Vue.prototype.$bus.$emit('cart-after-itemchanged', { item: record })
    }
  },
  [types.CART_UPD_SHIPPING] (state, shippingMethod) {
    state.shipping = shippingMethod
  },
  [types.CART_LOAD_CART] (state, quote) {
    state.groupedCart = quote.grouped || []
    state.cartItems = state.groupedCart.reduce((items, group) => items.concat(group.items || []), [])
    state.serviceFee = quote['service_fee'] || 0
    state.cartQuantity = quote.count || 0
    state.cartIsLoaded = true

    // Vue.prototype.$bus.$emit('order/PROCESS_QUEUE', { config: config }) // process checkout queue
    Vue.prototype.$bus.$emit('sync/PROCESS_QUEUE', { config }) // process checkout queue
    Vue.prototype.$bus.$emit('application-after-loaded')
    Vue.prototype.$bus.$emit('cart-after-loaded')
  },
  [types.CART_LOAD_CART_SERVER_TOKEN] (state, token) {
    state.cartServerToken = token
  },
  [types.CART_UPD_TOTALS] (state, { itemsAfterTotals, totals, platformTotalSegments }) {
    state.itemsAfterPlatformTotals = itemsAfterTotals
    state.platformTotals = totals
    state.platformTotalSegments = platformTotalSegments
    Vue.prototype.$bus.$emit('cart-after-updatetotals', { platformTotals: totals, platformTotalSegments: platformTotalSegments })
  },
  [types.CART_UPD_PAYMENT] (state, paymentMethod) {
    state.payment = paymentMethod
  },
  [types.CART_TOGGLE_MICROCART] (state) {
    state.isMicrocartOpen = !state.isMicrocartOpen
  },
  [types.CART_SET_COUNT] (state, count) {
    state.cartQuantity = count
  },
  [types.CART_LOAD_UPDATE] (state, data) {
    if (!data.grouped) return;

    if (data.hasOwnProperty('service_fee')) state.serviceFee = data['service_fee'] || 0
    if (data.hasOwnProperty('count')) state.cartQuantity = data.count || 0

    data.grouped.forEach(gr => {
      let group = state.groupedCart.find(g => g.partner.code === gr.partner.code)
      if (!group) {
        // First item added to a partner not yet represented in groupedCart (e.g. adding to a
        // currently-empty cart) - merging into existing groups only, as below, would silently
        // drop it and leave the cart looking empty despite cartItems already holding it.
        state.groupedCart.push(gr)
        return
      }
      Object.keys(group).forEach(key => {
        if (key === 'partner') return

        if (group[key] !== gr[key]) {
          group[key] = gr[key]
        }
      })
    })
  },
  [types.CART_USER_TOKEN_CHANGED] (state) {
    rootStore.state.user.token = null
    rootStore.state.user.refreshToken = null
    rootStore.state.user.current = null
  },
  [types.CART_LOADING_STATE] (state, payload: boolean) {
    state.isCartLoading = payload
  },
  [types.CART_DELIVERY_UNKNOWN] (state) {
    state.groupedCart.forEach(group => {
      group.delivery = null
      group.final_delivery = null
    })
  }
}

export default mutations
